<script setup lang="ts">
/**
 * Offline/sync status pill shown in the app header for authenticated users.
 * Derives all state from useSync — no additional network requests.
 *
 * Neutral text uses the on-banner --tag tone (not --muted-foreground): this pill
 * only ever rides the dark brand banner (#244), where --foreground/--muted would
 * vanish against the near-black --header-bg in both themes.
 *
 * Offline state is debounced (2 s) to avoid flashing on brief interruptions.
 */
import { ref, watch, onUnmounted } from 'vue'
import { useSync } from '../composables/useSync'

const { isSyncing, isOnline, hasPendingChanges } = useSync()

const showOffline = ref(!isOnline.value)
let offlineTimer: ReturnType<typeof setTimeout> | null = null

watch(isOnline, (online) => {
  if (online) {
    if (offlineTimer) {
      clearTimeout(offlineTimer)
      offlineTimer = null
    }
    showOffline.value = false
  } else {
    offlineTimer = setTimeout(() => {
      showOffline.value = true
      offlineTimer = null
    }, 2000)
  }
})

onUnmounted(() => {
  if (offlineTimer) clearTimeout(offlineTimer)
})
</script>

<template>
  <div class="flex items-center gap-1.5 text-xs" data-testid="sync-status">
    <template v-if="isSyncing">
      <span class="w-2 h-2 rounded-full bg-primary animate-pulse" aria-hidden="true"></span>
      <span class="text-[var(--tag)]">Syncing…</span>
    </template>
    <template v-else-if="showOffline">
      <span class="w-2 h-2 rounded-full bg-warning" aria-hidden="true"></span>
      <span
        class="text-warning"
        :title="
          hasPendingChanges
            ? 'Changes saved locally — will sync when back online'
            : 'You\'re offline'
        "
      >
        {{ hasPendingChanges ? 'Offline — changes pending' : 'Offline' }}
      </span>
    </template>
    <template v-else>
      <span class="w-2 h-2 rounded-full bg-success" aria-hidden="true"></span>
      <span class="text-[var(--tag)]">Synced</span>
    </template>
  </div>
</template>
